import classNames from 'classnames';
import { pluralize, titleize } from 'inflection';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { KeyboardEvent, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Popup } from 'semantic-ui-react';
import { v4 as uuid } from 'uuid';

import s from './key-value-editor.scss';

const DEFAULT_MAX_ROWS = 5;

export type KeyValueRecord = Record<string, string>;
export type KeyValueTuple = [string, string];
export type KeyValueTuples = KeyValueTuple[];

type KeyValueRow = { id: string; key: string; value: string };

/**
 * Creates a new row with a unique ID.
 */
const createRow = (key = '', value = ''): KeyValueRow => ({
  id: uuid(),
  key,
  value,
});

/**
 * Converts a key-value object to an array of key-value rows.
 */
export const recordToRows = (
  input: KeyValueRecord | undefined,
): KeyValueRow[] =>
  input
    ? Object.entries(input).map(([key, value]) => createRow(key, value))
    : [];

/**
 * Converts an array of key-value rows back to an object.
 *
 * If duplicate keys exist, the last one wins. Only leading/trailing whitespace
 * on keys is trimmed; values are stored as-is.
 */
export const rowsToRecord = (rows: KeyValueRow[]): KeyValueRecord =>
  rows.reduce<KeyValueRecord>((result, { key, value }) => {
    const trimmedKey = key.trim();

    if (trimmedKey) result[trimmedKey] = value.trim();

    return result;
  }, {});

/**
 * Converts an array of tuples to key-value rows.
 */
const tuplesToRows = (tuples: KeyValueTuples | undefined): KeyValueRow[] =>
  tuples ? tuples.map(([key, value]) => createRow(key, value)) : [];

/**
 * Converts key-value rows to an array of tuples, preserving duplicates.
 * Empty rows (blank key) are omitted; keys and values are trimmed.
 */
export const rowsToTuples = (rows: KeyValueRow[]): KeyValueTuples =>
  rows.reduce<KeyValueTuples>((result, { key, value }) => {
    const trimmedKey = key.trim();

    if (trimmedKey) result.push([trimmedKey, value.trim()]);

    return result;
  }, []);

/**
 * Returns the set of row indices whose trimmed key appears more than once.
 */
const findDuplicateKeyIndices = (rows: KeyValueRow[]): Set<number> => {
  const seen = new Map<string, number>();
  const duplicates = new Set<number>();

  rows.forEach(({ key }, index) => {
    const trimmed = key.trim();

    if (!trimmed) return;

    const existing = seen.get(trimmed);

    if (existing !== undefined) {
      duplicates.add(existing);
      duplicates.add(index);
    } else {
      seen.set(trimmed, index);
    }
  });

  return duplicates;
};

interface KeyValueEditorBaseProps {
  /**
   * Called when Enter is pressed on a completely empty row (both key and
   * value blank). Intended for form-level submission — when there is nothing
   * left to add, Enter submits instead of adding another row.
   */
  onEnterSubmit?: () => void;
  /**
   * When `true`, starts with one empty row if no value is provided.
   *
   * @default true
   */
  autoAddFirstRow?: boolean;
  /**
   * When `true`, focuses the first key input on mount.
   *
   * @default false
   */
  autoFocus?: boolean;
  /**
   * Content rendered between the rows and the add button. Useful for
   * displaying validation messages within the editor layout.
   */
  children?: ReactNode;
  /**
   * Additional CSS class applied to the root element.
   */
  className?: string;
  /**
   * Whether the editor is disabled.
   */
  disabled?: boolean;
  /**
   * Whether to hide the description text above the rows.
   *
   * @default false
   */
  hideDescription?: boolean;
  /**
   * The name or type of item being edited. Will be pluralized internally. Used
   * for display purposes.
   *
   * @default "item"
   * @example "annotation" -> "You can add up to 5 annotations", "+ Add Annotation"
   */
  itemName?: string;
  /**
   * Placeholder text for the key input.
   *
   * @default "Key"
   */
  keyPlaceholder?: string;
  /**
   * Maximum character length for key and/or value inputs.
   *
   * - `number` — applies the same limit to both key and value
   * - `[number | undefined, number]` — first element is the key limit, second
   *   is the value limit; use `undefined` to skip a limit for one side
   */
  maxLength?: number | [number | undefined, number];
  /**
   * Maximum number of rows the editor allows.
   *
   * @default 5
   */
  maxRows?: number;
  /**
   * Callback fired when the overall validity of all rows changes.
   *
   * A completely empty row (both key and value blank) is ignored — it does not
   * count as invalid. Only partially filled rows and duplicate keys affect
   * validity. The second argument indicates whether duplicate keys exist.
   */
  onValidChange?: (valid: boolean, hasDuplicates: boolean) => void;
  /**
   * When `true`, highlights rows with duplicate keys. Intended for use with
   * `validateDuplicateKeys={false}` so the parent can control when highlights
   * appear (e.g. after a form validation pass).
   *
   * @default false
   */
  showDuplicateErrors?: boolean;
  /**
   * Whether the editor self-validates and highlights duplicate keys. Set to
   * `false` when duplicate validation is managed externally (e.g. by a form
   * framework) and the highlights should only appear on demand via
   * `showDuplicateErrors`.
   *
   * @default true
   */
  validateDuplicateKeys?: boolean;
  /**
   * Placeholder text for the value input.
   *
   * @default "Value"
   */
  valuePlaceholder?: string;
  /**
   * Whether a non-empty value is required for a row to be considered valid.
   *
   * @default true
   */
  valueRequired?: boolean;
}

/**
 * Record output format (default). Emits a `Record<string, string>` on change.
 * Duplicate keys are collapsed (last writer wins).
 */
interface KeyValueEditorRecordProps extends KeyValueEditorBaseProps {
  /**
   * Callback fired on every row change (add, remove, or edit).
   */
  onChange: (value: KeyValueRecord) => void;
  /**
   * Output format. When `'record'` (default), emits a `Record<string, string>`.
   *
   * @default "record"
   */
  outputFormat?: 'record';
  /**
   * Current value as a Record of key-value pairs.
   */
  value?: KeyValueRecord;
}

/**
 * Tuple output format. Emits a `[string, string][]` on change, preserving
 * duplicate keys. Use this when the consumer needs to detect or validate
 * duplicate keys (e.g. via a Zod schema).
 */
interface KeyValueEditorTupleProps extends KeyValueEditorBaseProps {
  /**
   * Callback fired on every row change (add, remove, or edit).
   */
  onChange: (value: KeyValueTuples) => void;
  /**
   * Output format. When `'tuples'`, emits a `[string, string][]` that
   * preserves duplicate keys.
   */
  outputFormat: 'tuples';
  /**
   * Current value as an array of `[key, value]` tuples.
   */
  value?: KeyValueTuples;
}

export type KeyValueEditorProps =
  KeyValueEditorRecordProps | KeyValueEditorTupleProps;

/**
 * Inline key-value pair editor. Renders rows of key/value inputs with add and
 * remove controls. Changes are reported live via `onChange` on every edit.
 *
 * For a dropdown-based variant with Apply/Cancel buffering, see
 * `KeyValueEditorDropdown`.
 *
 * @see KeyValueEditorDropdown
 */
export const KeyValueEditor = ({
  autoAddFirstRow = true,
  autoFocus = false,
  className,
  disabled,
  children,
  itemName = 'item',
  keyPlaceholder = 'Key',
  maxLength,
  maxRows = DEFAULT_MAX_ROWS,
  onChange,
  onEnterSubmit,
  onValidChange,
  hideDescription,
  outputFormat = 'record',
  showDuplicateErrors = false,
  validateDuplicateKeys = true,
  value: externalValue,
  valuePlaceholder = 'Value',
  valueRequired = true,
}: KeyValueEditorProps) => {
  const keyMaxLength = Array.isArray(maxLength) ? maxLength[0] : maxLength;
  const valueMaxLength = Array.isArray(maxLength) ? maxLength[1] : maxLength;
  useStyles(s);

  const parseValue = outputFormat === 'tuples' ? tuplesToRows : recordToRows;

  const firstKeyInputRef = useRef<HTMLInputElement>(null);
  const newRowKeyInputRef = useRef<HTMLInputElement>(null);

  const [rows, setRows] = useState<KeyValueRow[]>(() => {
    const initial = parseValue(
      externalValue as KeyValueRecord & KeyValueTuples,
    );
    return initial.length === 0 && autoAddFirstRow ? [createRow()] : initial;
  });

  // Always-current snapshot of rows, used by callbacks that need the latest
  // state without taking a rows dependency (which would re-create them every
  // edit and risks stale closures in rapid-fire sequences).
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const [pendingFocusIndex, setPendingFocusIndex] = useState<number | null>(
    null,
  );

  // Track whether the last value change originated from within the editor so
  // the sync effect can skip the round-trip and avoid overwriting in-progress
  // edits.
  const internalChangeRef = useRef(false);

  // Skip the first sync run — useState already initialised rows from
  // externalValue. Without this guard the effect would recreate the initial
  // row(s) with new UUIDs on mount, causing a needless remount that breaks
  // autoFocus and flickers the cursor.
  //
  // Note: useIsMounted() from usehooks-ts cannot substitute here because its
  // internal effect fires before this one (effects run in hook-call order),
  // so isMounted() would already return true on the first sync run.
  const syncHasRunRef = useRef(false);

  // Sync with external value changes. Skipped on mount (handled by useState)
  // and when the change came from the editor itself (via emitChange).
  useEffect(() => {
    if (!syncHasRunRef.current) {
      syncHasRunRef.current = true;
      return;
    }

    if (internalChangeRef.current) {
      internalChangeRef.current = false;
      return;
    }

    const next = parseValue(externalValue as KeyValueRecord & KeyValueTuples);
    setRows(next.length === 0 && autoAddFirstRow ? [createRow()] : next);
  }, [autoAddFirstRow, externalValue, parseValue]);

  const isRowEmpty = useCallback(
    (row: KeyValueRow): boolean =>
      row.key.trim() === '' && row.value.trim() === '',
    [],
  );

  const isRowValid = useCallback(
    (row: KeyValueRow): boolean => {
      if (isRowEmpty(row)) return true;
      return (
        row.key.trim() !== '' && (!valueRequired || row.value.trim() !== '')
      );
    },
    [isRowEmpty, valueRequired],
  );

  const emitChange = useCallback(
    (nextRows: KeyValueRow[]) => {
      internalChangeRef.current = true;

      if (outputFormat === 'tuples') {
        (onChange as (v: KeyValueTuples) => void)(rowsToTuples(nextRows));
      } else {
        (onChange as (v: KeyValueRecord) => void)(rowsToRecord(nextRows));
      }
    },
    [onChange, outputFormat],
  );

  const updateRow = useCallback(
    (index: number, field: 'key' | 'value', newValue: string) => {
      const updated = [...rowsRef.current];
      updated[index] = { ...updated[index], [field]: newValue };
      setRows(updated);
      emitChange(updated);
    },
    [emitChange],
  );

  const removeRow = useCallback(
    (indexToRemove: number) => {
      const updated = rowsRef.current.filter((_, i) => i !== indexToRemove);
      setRows(updated);
      emitChange(updated);
    },
    [emitChange],
  );

  const addRow = useCallback(() => {
    setRows(current => {
      const newIndex = current.length;
      setPendingFocusIndex(newIndex);
      return [...current, createRow()];
    });
  }, []);

  // Focus the first key input on mount when autoFocus is enabled.
  // setTimeout defers past all pending React state-update microtasks so the
  // DOM is fully settled before we steal focus (requestAnimationFrame alone is
  // not enough when the host Modal runs its own requestAnimationFrame loop that can trigger
  // further re-renders).
  useEffect(() => {
    if (!autoFocus) return;
    const timeoutId = setTimeout(() => firstKeyInputRef.current?.focus(), 0);
    return () => clearTimeout(timeoutId);
  }, [autoFocus]);

  // Focus the key input of a newly added row.
  useEffect(() => {
    if (pendingFocusIndex === null || !newRowKeyInputRef.current) return;

    newRowKeyInputRef.current.focus();
    setPendingFocusIndex(null);
  }, [pendingFocusIndex, rows]);

  const duplicateIndices = useMemo(() => findDuplicateKeyIndices(rows), [rows]);
  const hasDuplicates = duplicateIndices.size > 0;
  const allRowsValid = rows.every(isRowValid) && !hasDuplicates;

  // Notify the parent when row validity changes.
  useEffect(() => {
    onValidChange?.(allRowsValid, hasDuplicates);
  }, [allRowsValid, hasDuplicates, onValidChange]);

  const lastRow = rows[rows.length - 1];
  const hasMaxRows = rows.length >= maxRows;
  const canAddRow =
    !hasMaxRows && (!lastRow || (!isRowEmpty(lastRow) && isRowValid(lastRow)));

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>, index: number) => {
      if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;

      // Always prevent default to stop form submission on Enter.
      event.preventDefault();

      const row = rowsRef.current[index];
      const trailingRow = rowsRef.current[rowsRef.current.length - 1];

      // Empty row — delegate to form submission when available.
      if (!row || isRowEmpty(row)) {
        onEnterSubmit?.();
        return;
      }

      if (!isRowValid(row)) return;

      // Can add a new row — do that.
      if (
        rowsRef.current.length < maxRows &&
        (!trailingRow || !isRowEmpty(trailingRow))
      ) {
        addRow();
        return;
      }

      // Row is filled and valid but can't grow (max reached or trailing
      // empty row exists) — delegate to form submission.
      onEnterSubmit?.();
    },
    [addRow, isRowEmpty, isRowValid, maxRows, onEnterSubmit],
  );

  const itemNameTitleCase = titleize(itemName);
  const itemNamePlural = pluralize(itemName);
  const description = `You can add up to ${maxRows} ${itemNamePlural}`;
  const addButtonText = `Add ${itemNameTitleCase}`;
  const addButtonTooltip =
    lastRow && (isRowEmpty(lastRow) || !isRowValid(lastRow))
      ? `Please enter a valid key${valueRequired ? ' and value' : ''} above to add another ${itemName}.`
      : undefined;

  return (
    <div
      className={classNames(s.editorPanel, className)}
      role="group"
      aria-label={`${itemNameTitleCase} editor`}
    >
      {!hideDescription && <div className={s.description}>{description}</div>}

      {rows.length > 0 && (
        <div className={s.rows}>
          {rows.map((row, index) => {
            const isDuplicate =
              (validateDuplicateKeys || showDuplicateErrors) &&
              duplicateIndices.has(index);

            return (
              <div
                key={row.id}
                className={s.row}
                role="group"
                aria-label={`${itemNameTitleCase} ${index + 1}`}
              >
                <Input
                  ref={
                    index === 0
                      ? firstKeyInputRef
                      : index === pendingFocusIndex
                        ? newRowKeyInputRef
                        : undefined
                  }
                  aria-label={`Key ${index + 1}`}
                  className={s.keyInput}
                  disabled={disabled}
                  maxLength={keyMaxLength}
                  name={`keyInput${index + 1}`}
                  placeholder={keyPlaceholder}
                  value={row.key}
                  onChange={(_, data) => updateRow(index, 'key', data.value)}
                  onKeyDown={(event: KeyboardEvent<HTMLInputElement>) =>
                    handleInputKeyDown(event, index)
                  }
                  size="small"
                  error={isDuplicate}
                />

                <Input
                  aria-label={`Value ${index + 1}`}
                  className={s.valueInput}
                  disabled={disabled}
                  maxLength={valueMaxLength}
                  name={`valueInput${index + 1}`}
                  placeholder={valuePlaceholder}
                  value={row.value}
                  onChange={(_, data) => updateRow(index, 'value', data.value)}
                  onKeyDown={(event: KeyboardEvent<HTMLInputElement>) =>
                    handleInputKeyDown(event, index)
                  }
                  size="small"
                />

                <Button
                  aria-label={`Remove ${itemName} ${index + 1}`}
                  className={s.deleteButton}
                  disabled={disabled}
                  icon="trash"
                  size="small"
                  onClick={() => removeRow(index)}
                />
              </div>
            );
          })}
        </div>
      )}

      {children}

      {!hasMaxRows && (
        <Popup
          content={addButtonTooltip}
          disabled={canAddRow}
          on="hover"
          position="bottom center"
          trigger={
            <span>
              <Button
                aria-label={`Add ${itemName}`}
                className={s.addButton}
                color="grey"
                content={addButtonText}
                disabled={disabled || !canAddRow}
                icon="add"
                size="small"
                onClick={addRow}
                basic
                fluid
              />
            </span>
          }
          flowing
        />
      )}
    </div>
  );
};

export default KeyValueEditor;
